import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Cpu, DollarSign } from 'lucide-react';
import { ProjectKanbanCard } from './ProjectKanbanCard';
import { formatCompactCurrency } from '@/lib/utils';
import type { KanbanColumn, ProjectKanbanCard as CardType } from '../types';

// Rendering cap per column — keeps busy boards fast without hiding data:
// every card stays in the payload and reachable via the "Show more" control.
const COLUMN_RENDER_LIMIT = 25;

interface ProjectKanbanColumnProps {
  column: KanbanColumn;
  activeProjectId?: string | null;
  onProjectClick: (project: CardType) => void;
  onCreateInvoice?: (project: CardType) => void;
}

export function ProjectKanbanColumn({
  column,
  activeProjectId,
  onProjectClick,
  onCreateInvoice,
}: ProjectKanbanColumnProps) {
  const { t } = useTranslation('operations');
  const [visibleCount, setVisibleCount] = useState(COLUMN_RENDER_LIMIT);
  const { setNodeRef, isOver } = useDroppable({
    id: column.stage,
    data: { type: 'column', stage: column.stage },
  });

  const visibleProjects = column.projects.slice(0, visibleCount);
  const hiddenCount = column.projects.length - visibleProjects.length;
  const projectIds = visibleProjects.map((p) => p.id);

  return (
    <div
      className={`flex flex-col rounded-lg border bg-muted/30 min-w-[280px] w-[280px] shrink-0 ${
        isOver ? 'ring-2 ring-primary ring-offset-2' : ''
      }`}
    >
      {/* Column header */}
      <div className="p-3 border-b space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold truncate">{column.label}</h3>
          <span className="text-xs font-medium text-muted-foreground bg-muted rounded-full px-2 py-0.5">
            {column.totals.projectCount}
          </span>
        </div>

        <div className="text-xs font-medium text-primary">Value $ {column.valuePercent}%</div>

        {/* Aggregates */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-xs bg-cyan-50 text-cyan-700 rounded px-1.5 py-0.5">
            <Cpu className="h-3 w-3" />
            <span>{column.totals.sensorsQty} qty</span>
          </div>
          <div className="flex items-center gap-1 text-xs bg-green-50 text-green-700 rounded px-1.5 py-0.5">
            <DollarSign className="h-3 w-3" />
            <span>{formatCompactCurrency(column.totals.value)}</span>
          </div>
        </div>

      </div>

      {/* Cards area */}
      <div
        ref={setNodeRef}
        className={`flex-1 overflow-y-auto p-2 space-y-2 min-h-[200px] ${isOver ? 'bg-primary/5' : ''}`}
      >
        <SortableContext items={projectIds} strategy={verticalListSortingStrategy}>
          {visibleProjects.map((project) => (
            <ProjectKanbanCard
              key={project.id}
              project={project}
              onClick={() => onProjectClick(project)}
              onCreateInvoice={
                column.stage === 'invoicing' && onCreateInvoice
                  ? () => onCreateInvoice(project)
                  : undefined
              }
              isDragging={activeProjectId === project.id}
            />
          ))}
        </SortableContext>

        {hiddenCount > 0 && (
          <button
            type="button"
            className="w-full rounded-md border border-dashed px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition"
            onClick={() => setVisibleCount((count) => count + COLUMN_RENDER_LIMIT)}
          >
            {t('kanban.showMore', 'Show {{n}} more', { n: hiddenCount })}
          </button>
        )}
      </div>
    </div>
  );
}
